"use client";

import { useQuery } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import { tournamentKeys } from "@/shared/api/query-keys";

export interface OfficialStreamData {
  channel: string;
  channelUrl: string;
  isLive: boolean;
  title?: string | null;
  viewerCount?: number | null;
  /** KAN-656: `unknown` — до Twitch не достучались, это не «офлайн». */
  status?: "live" | "offline" | "unknown";
}

export interface PlayerStreamData {
  userId: string;
  twitchLogin: string;
  channelUrl: string;
  isLive: boolean;
  /** Откуда логин: подтверждённая OAuth-привязка или строка из профиля (KAN-655). */
  source?: "oauth" | "profile";
  /**
   * KAN-656: три состояния эфира. `unknown` — Twitch не ответил, и это НЕ «офлайн».
   * Опционально: пока бэкенд не выкачен, поля нет и читаем по isLive.
   */
  status?: StreamStatus;
}

export type StreamStatus = "live" | "offline" | "unknown";

export interface TournamentStreamsData {
  officialStream: OfficialStreamData | null;
  playerStreams: PlayerStreamData[];
  /**
   * KAN-655: false — показ стримеров-игроков выключен в настройках турнира.
   * Пустой список без этого признака выглядел одинаково и при выключенном
   * тумблере, и при «никто не привязал канал».
   *
   * Опционально: старый бэкенд поля не отдаёт, и до его выката отсутствие
   * значения должно читаться как «включено», а не как «выключено».
   */
  showPlayerStreamers?: boolean;

  /**
   * KAN-656: true — до Twitch не достучались, и статусы в этом ответе значат
   * «не знаем», а не «офлайн».
   */
  streamStatusUnavailable?: boolean;
}

const STREAMS_REFETCH_INTERVAL_MS = 75_000;

async function fetchTournamentStreams(tournamentId: string) {
  const res = await api.get<TournamentStreamsData>(
    `/tournaments/${encodeURIComponent(tournamentId)}/streams`,
  );
  return res.data;
}

/**
 * Батч live-статусы Twitch турнира (KAN-90): официальный стрим + стримы
 * игроков-участников одним запросом. Используется для иконок в гридах
 * команд/участников (KAN-93). Не сгенерирован через Kubb — backend ещё не
 * задеплоен на тест, эндпоинт вызывается напрямую через api-client.
 */
export function useTournamentStreams(tournamentId: string | undefined) {
  return useQuery({
    queryKey: tournamentKeys.streams(tournamentId ?? ""),
    queryFn: () => fetchTournamentStreams(tournamentId!),
    enabled: Boolean(tournamentId),
    staleTime: 30_000,
    refetchInterval: STREAMS_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
